import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { ChangelogPlatform } from '../../data/changelog';
import Footer from '../../components/Footer';
import { useStatusData } from './useStatusData';
import { derivePlatform, latestVersionForPlatform } from './statusData';
import ServiceStatusSection from './ServiceStatusSection';
import MaintenanceNotice from './MaintenanceNotice';
import ReleaseHistory from './ReleaseHistory';
import BetaGuidance from './BetaGuidance';
import './StatusPage.css';
import './StatusLayout.css';

const StatusPage: React.FC = () => {
  const { colors } = useTheme();
  const {
    changelog,
    changelogError,
    systemStatus,
    systemStatusError,
    maintenanceWindows,
    loading,
    betaStatus,
    retry,
  } = useStatusData();

  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedBuild, setSelectedBuild] = useState<string>('all');
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showBuildDropdown, setShowBuildDropdown] = useState(false);
  // Public changelogs are strictly separated by platform — exactly two
  // values, no combined/"all" view, per the owner's product decision.
  const [selectedPlatform, setSelectedPlatform] = useState<ChangelogPlatform>('ios');

  // Get unique build versions from changelog, scoped to the selected platform.
  // Platform is derived from the version string (no platform column in DB).
  const buildVersions = React.useMemo(() => {
    const versions = new Set(
      changelog
        .filter(entry => derivePlatform(entry.version) === selectedPlatform)
        .map(entry => entry.version)
    );
    return Array.from(versions).sort((a, b) => {
      const numA = parseInt(a.replace(/[^0-9]/g, ''));
      const numB = parseInt(b.replace(/[^0-9]/g, ''));
      if (isNaN(numA) && isNaN(numB)) return 0;
      if (isNaN(numA)) return 1;
      if (isNaN(numB)) return -1;
      return numB - numA; // Descending order
    });
  }, [changelog, selectedPlatform]);

  // Platform summary cards derive independently from all published
  // changelog entries — a Release History tab/category/build selection
  // must never move either summary.
  const latestIosVersion = React.useMemo(
    () => latestVersionForPlatform(changelog, 'ios'),
    [changelog]
  );
  const latestAndroidVersion = React.useMemo(
    () => latestVersionForPlatform(changelog, 'android'),
    [changelog]
  );

  // Filter changelog based on selected filters. Platform is always applied —
  // public changelogs are strictly separated by platform, never combined.
  const filteredChangelog = React.useMemo(() => {
    return changelog.filter(entry => {
      const platformMatch = derivePlatform(entry.version) === selectedPlatform;
      const categoryMatch = selectedCategory === 'all' || entry.category === selectedCategory;
      const buildMatch = selectedBuild === 'all' || entry.version === selectedBuild;
      return platformMatch && categoryMatch && buildMatch;
    });
  }, [changelog, selectedCategory, selectedBuild, selectedPlatform]);

  const platformTotal = React.useMemo(
    () => changelog.filter(e => derivePlatform(e.version) === selectedPlatform).length,
    [changelog, selectedPlatform]
  );

  const closeDropdowns = () => {
    setShowCategoryDropdown(false);
    setShowBuildDropdown(false);
  };

  const betaVisible = betaStatus?.visible !== false;

  return (
    <div className="status-page" style={{ backgroundColor: colors.background }}>
      <main className="status-main pt-20 md:pt-24 pb-16">
        <header className="mb-10">
          <h1
            className="text-3xl sm:text-4xl font-bold mb-2 leading-tight"
            style={{ color: colors.textPrimary }}
          >
            Status &amp; updates
          </h1>
          <p style={{ color: colors.textSecondary }}>
            Service availability, maintenance, and the latest RidePulse releases.
          </p>
        </header>

        <div className="status-sections">
          <ServiceStatusSection
            services={systemStatus}
            loading={loading}
            error={systemStatusError}
            onRetry={retry}
          />

          <MaintenanceNotice windows={maintenanceWindows} />

          <div className={`status-editorial-layout${betaVisible ? '' : ' status-editorial-single'}`}>
            <ReleaseHistory
              entries={filteredChangelog}
              platformTotal={platformTotal}
              hasAnyEntries={changelog.length > 0}
              loading={loading}
              error={changelogError}
              onRetry={retry}
              selectedPlatform={selectedPlatform}
              onPlatformChange={(platform) => {
                setSelectedPlatform(platform);
                setSelectedBuild('all');
              }}
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
              selectedBuild={selectedBuild}
              onBuildChange={setSelectedBuild}
              buildVersions={buildVersions}
              showCategoryDropdown={showCategoryDropdown}
              showBuildDropdown={showBuildDropdown}
              onToggleCategory={() => {
                setShowCategoryDropdown(!showCategoryDropdown);
                setShowBuildDropdown(false);
              }}
              onToggleBuild={() => {
                setShowBuildDropdown(!showBuildDropdown);
                setShowCategoryDropdown(false);
              }}
              onCloseDropdowns={closeDropdowns}
            />

            <BetaGuidance
              betaStatus={betaStatus}
              latestIosVersion={latestIosVersion}
              latestAndroidVersion={latestAndroidVersion}
              loading={loading}
            />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default StatusPage;
